import { useState, useRef, type ChangeEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Button, Text } from "@lendcord/concord";
import {
  useRequestDocumentUploadUrlMutation,
  uploadDocumentFile,
  useConfirmDocumentUploadMutation,
} from "@/features/profile/api/profile.api";
import { MAX_IMAGE_UPLOAD_SIZE_BYTES, MAX_PDF_UPLOAD_SIZE_BYTES, MIN_DOCUMENT_UPLOAD_SIZE_BYTES } from "@/app/profile/documentUploadLimits";
import { compressImageToLimit } from "@/app/profile/imageCompression";
import type { DocumentRef } from "./onboarding.types";
import type { DocumentType } from "@/app/profile/profile.types";

interface DocumentUploadSectionProps {
  documents: DocumentRef[];
  onDocumentsChange: (documents: DocumentRef[]) => void;
}

const MAX_ADDITIONAL = 10;

const FIXED_SLOTS = ["PAN", "AADHAAR_FRONT", "AADHAAR_BACK"] as const;

function slotLabelId(slot: string): string {
  switch (slot) {
    case "PAN": return "lender.onboarding.documentPanLabel";
    case "AADHAAR_FRONT": return "lender.onboarding.documentAadhaarFrontLabel";
    case "AADHAAR_BACK": return "lender.onboarding.documentAadhaarBackLabel";
    default: return "";
  }
}

function apiType(slot: string): DocumentType {
  if (slot === "AADHAAR_FRONT" || slot === "AADHAAR_BACK") return slot;
  // ADDITIONAL_DOCUMENT_1..10 are real, distinct backend types (see the
  // Postman collection) — pass the slot straight through instead of
  // collapsing every extra document into an undifferentiated "OTHER".
  if (slot.startsWith("ADDITIONAL_DOCUMENT_")) return slot as DocumentType;
  return "PAN";
}

function nextExtraId(documents: DocumentRef[]): number {
  const existing = documents
    .filter((d) => d.type.startsWith("ADDITIONAL_DOCUMENT_"))
    .map((d) => {
      const parts = d.type.split("_");
      return Number(parts[parts.length - 1]);
    })
    .filter((n) => !isNaN(n));
  for (let i = 1; i <= MAX_ADDITIONAL; i++) {
    if (!existing.includes(i)) return i;
  }
  return MAX_ADDITIONAL + 1;
}

export function DocumentUploadSection({ documents, onDocumentsChange }: DocumentUploadSectionProps) {
  const intl = useIntl();
  const [requestDocumentUploadUrl] = useRequestDocumentUploadUrlMutation();
  const [confirmDocumentUpload] = useConfirmDocumentUploadMutation();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingSlotRef = useRef<string | null>(null);

  const [uploading, setUploading] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  const extraIds = documents
    .filter((d) => d.type.startsWith("ADDITIONAL_DOCUMENT_"))
    .map((d) => Number(d.type.split("_").pop()))
    .filter((n) => !isNaN(n))
    .sort((a, b) => a - b);

  function isUploaded(type: string) {
    return documents.some((d) => d.type === type);
  }

  function removeDocument(type: string) {
    onDocumentsChange(documents.filter((d) => d.type !== type));
  }

  function triggerUpload(type: string) {
    pendingSlotRef.current = type;
    fileInputRef.current?.click();
  }

  async function handleFileSelected(event: ChangeEvent<HTMLInputElement>) {
    let file = event.target.files?.[0];
    event.target.value = "";
    const type = pendingSlotRef.current;
    pendingSlotRef.current = null;
    if (!file || !type || uploading) return;

    setErrors((prev) => ({ ...prev, [type]: "" }));

    if (file.size < MIN_DOCUMENT_UPLOAD_SIZE_BYTES) {
      setErrors((prev) => ({ ...prev, [type]: intl.formatMessage({ id: "lender.profile.documentUploadTooSmall" }) }));
      return;
    }

    const isImage = file.type.startsWith("image/");
    if (isImage && file.size > MAX_IMAGE_UPLOAD_SIZE_BYTES) {
      file = await compressImageToLimit(file, MAX_IMAGE_UPLOAD_SIZE_BYTES);
    }
    const maxBytes = isImage ? MAX_IMAGE_UPLOAD_SIZE_BYTES : MAX_PDF_UPLOAD_SIZE_BYTES;
    if (file.size > maxBytes) {
      setErrors((prev) => ({ ...prev, [type]: intl.formatMessage({ id: "lender.profile.documentUploadTooLarge" }) }));
      return;
    }

    setUploading(type);
    try {
      const { storageKey, uploadUrl } = await requestDocumentUploadUrl(apiType(type)).unwrap();
      await uploadDocumentFile(uploadUrl, file);
      await confirmDocumentUpload({ type: apiType(type), storageKey }).unwrap();

      removeDocument(type);
      onDocumentsChange([...documents, { type, storageKey }]);
    } catch {
      setErrors((prev) => ({ ...prev, [type]: intl.formatMessage({ id: "lender.profile.documentUploadError" }) }));
    } finally {
      setUploading(null);
    }
  }

  function addExtraSlot() {
    const id = nextExtraId(documents);
    if (id > MAX_ADDITIONAL) return;
    triggerUpload(`ADDITIONAL_DOCUMENT_${id}`);
  }

  function renderFixedSlot(type: string) {
    return (
      <div key={type} className="flex flex-col gap-2">
        <Text as="p" size="sm" className="font-medium">
          <FormattedMessage id={slotLabelId(type)} />
        </Text>
        {errors[type] && <AlertBanner status="error">{errors[type]}</AlertBanner>}
        {isUploaded(type) ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-accent-600">✓</span>
            <Text as="span" size="sm" variant="muted">
              <FormattedMessage id="lender.onboarding.documentUploaded" />
            </Text>
            <button type="button" onClick={() => removeDocument(type)} className="text-xs text-error-fg hover:underline">
              <FormattedMessage id="lender.onboarding.documentRemove" />
            </button>
          </div>
        ) : (
          <Button
            type="button"
            variant="secondary"
            density="compact"
            onClick={() => triggerUpload(type)}
            disabled={uploading === type}
            className="self-start"
          >
            {uploading === type ? (
              <FormattedMessage id="lender.profile.uploadingButton" />
            ) : (
              <FormattedMessage id="lender.onboarding.documentUploadButton" />
            )}
          </Button>
        )}
      </div>
    );
  }

  return (
    <section className="px-6 py-3">
      <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
        {FIXED_SLOTS.map(renderFixedSlot)}
      </div>

      <div className="mt-6 border-t border-border pt-6">
        <Text as="p" size="sm" className="mb-4 font-medium">
          <FormattedMessage id="lender.onboarding.additionalDocumentsHeading" />
        </Text>
        {extraIds.length > 0 && (
          <div className="mb-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {extraIds.map((id) => {
              const type = `ADDITIONAL_DOCUMENT_${id}`;
              return (
                <div key={type} className="flex flex-col gap-2">
                  <Text as="p" size="sm" className="font-medium">
                    <FormattedMessage id="lender.onboarding.additionalDocumentLabel" values={{ id }} />
                  </Text>
                  {errors[type] && <AlertBanner status="error">{errors[type]}</AlertBanner>}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-accent-600">✓</span>
                    <Text as="span" size="sm" variant="muted">
                      <FormattedMessage id="lender.onboarding.documentUploaded" />
                    </Text>
                    <button type="button" onClick={() => removeDocument(type)} className="text-xs text-error-fg hover:underline">
                      <FormattedMessage id="lender.onboarding.documentRemove" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {extraIds.length < MAX_ADDITIONAL && (
          <Button
            type="button"
            variant="secondary"
            density="compact"
            onClick={addExtraSlot}
            disabled={!!uploading}
            className="self-start"
          >
            {uploading ? (
              <FormattedMessage id="lender.profile.uploadingButton" />
            ) : (
              <FormattedMessage id="lender.onboarding.addDocumentButton" />
            )}
          </Button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf"
        className="hidden"
        onChange={handleFileSelected}
      />
    </section>
  );
}